import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDownloadQuery } from '@/contexts/downloadQueryContextBase';
import { useDoctorActivityQuery } from '@/hooks/useDoctorActivityQuery';
import { useToast } from '@/contexts/toastContextBase';
import { getActivities, getActivitiesAll } from '@/api/activities';
import { downloadBlob } from '@/utils/download';
import type { DownloadStructure } from '@/types/download';
import { applyActivityFilters } from '@/utils/activityFilters';
import {
  getStoredDownloadStructure,
  setStoredDownloadStructure,
} from '@/utils/downloadStructure';
import {
  dedupeVibrationModes,
  getPersistedVibrationModes,
  persistDiscoveredVibrationModes,
} from '@/utils/vibrationModes';
import {
  buildDoctorDownloadRequest,
  getDoctorDownloadModeConfig,
  type DownloadMode,
} from './downloadWorkflow';

export function useDoctorDownloadController(mode: DownloadMode) {
  const { showToast } = useToast();
  const config = useMemo(() => getDoctorDownloadModeConfig(mode), [mode]);
  const [downloadStructure, setDownloadStructureState] = useState<DownloadStructure>(
    () => getStoredDownloadStructure()
  );

  const {
    query,
    preview,
    isLoadingPreview,
    downloadProgress,
    allPatients,
    allActivities,
    setPatients,
    setActivityTypes,
    setVibrationModes,
    setDateRange,
    clearFilters,
    setAllPatients,
    setAllActivities,
    loadPreview,
    setDownloadProgress,
    hasSelection,
    selectedActivities,
    selectedCount,
    patientCount,
    selectedActivityIds,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
  } = useDownloadQuery();

  const {
    patients,
    activities,
    isLoadingPatients,
    isLoadingActivities,
  } = useDoctorActivityQuery({
    patientIds: query.patientIds,
    shouldLoad: hasSelection,
    loadActivities: config.loadActivities === 'raw' ? getActivitiesAll : getActivities,
  });

  useEffect(() => {
    setAllPatients(patients);
  }, [patients, setAllPatients]);

  useEffect(() => {
    setAllActivities(activities);
  }, [activities, setAllActivities]);

  useEffect(() => {
    persistDiscoveredVibrationModes(allActivities.map((activity) => activity.vibrationMode));
  }, [allActivities]);

  useEffect(() => {
    if (hasSelection && allPatients.length > 0) {
      loadPreview();
    }
  }, [hasSelection, query, allPatients.length, loadPreview]);

  const availableVibrationModes = useMemo(
    () =>
      dedupeVibrationModes([
        ...getPersistedVibrationModes(),
        ...allActivities.map((activity) => activity.vibrationMode),
        ...query.vibrationModes,
      ]),
    [allActivities, query.vibrationModes]
  );

  const currentlyAvailableVibrationModes = useMemo(() => {
    let scopedActivities = allActivities;

    if (query.patientIds.length > 0) {
      scopedActivities = scopedActivities.filter((activity) =>
        query.patientIds.includes(activity.patientId)
      );
    }

    scopedActivities = applyActivityFilters(scopedActivities, {
      types: query.activityTypes,
      startDate: query.dateRange.from ?? undefined,
      endDate: query.dateRange.to ?? undefined,
    });

    return new Set(scopedActivities.map((activity) => activity.vibrationMode));
  }, [allActivities, query.activityTypes, query.dateRange.from, query.dateRange.to, query.patientIds]);

  const setDownloadStructure = useCallback((value: DownloadStructure) => {
    setDownloadStructureState(value);
    setStoredDownloadStructure(value);
  }, []);

  const handleDownload = useCallback(async () => {
    if (selectedActivities.length === 0) return;

    const { request, uniquePatientCount } = buildDoctorDownloadRequest(
      selectedActivities,
      allPatients
    );
    setDownloadProgress({ status: 'fetching', current: 0, total: uniquePatientCount });

    try {
      setDownloadProgress({
        status: 'fetching',
        current: Math.floor(uniquePatientCount / 2),
        total: uniquePatientCount,
      });

      const blob = await config.downloadAction(request, downloadStructure);

      setDownloadProgress({
        status: 'packaging',
        current: uniquePatientCount,
        total: uniquePatientCount,
      });

      downloadBlob(blob, `${config.filenamePrefix}-${new Date().toISOString().split('T')[0]}.zip`);

      setDownloadProgress({
        status: 'complete',
        current: uniquePatientCount,
        total: uniquePatientCount,
      });
      showToast(
        'success',
        `Downloaded ${selectedActivities.length} ${config.downloadLabel} from ${uniquePatientCount} patients`
      );

      setTimeout(() => {
        setDownloadProgress({ status: 'idle', current: 0, total: 0 });
      }, 2000);
    } catch (error) {
      setDownloadProgress({
        status: 'error',
        current: 0,
        total: 0,
        message: error instanceof Error ? error.message : 'Download failed',
      });
      showToast('error', `Failed to download ${config.downloadLabel}`);
    }
  }, [
    selectedActivities,
    allPatients,
    setDownloadProgress,
    showToast,
    config,
    downloadStructure,
  ]);

  return {
    config,
    query,
    downloadStructure,
    availableVibrationModes,
    currentlyAvailableVibrationModes,
    preview,
    isLoadingPreview,
    downloadProgress,
    allPatients,
    hasSelection,
    selectedCount,
    patientCount,
    selectedActivityIds,
    isLoadingPatients,
    isLoadingActivities,
    setPatients,
    setActivityTypes,
    setVibrationModes,
    setDownloadStructure,
    setDateRange,
    clearFilters,
    toggleActivitySelection,
    selectAllActivities,
    deselectAllActivities,
    handleDownload,
  };
}
